/**
 * SEAL Candidate Review UI
 * Phase 3, Step 3.2 — Admin page for reviewing improvement candidates
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import adminApi from '../../services/adminApi';
import './SealCandidates.css';

const PRIORITY_CONFIG = {
  critical: { label: 'Critical', color: '#dc2626', bg: '#fef2f2', icon: '🔴' },
  high: { label: 'High', color: '#ea580c', bg: '#fff7ed', icon: '🟠' },
  medium: { label: 'Medium', color: '#ca8a04', bg: '#fefce8', icon: '🟡' },
  low: { label: 'Low', color: '#6b7280', bg: '#f9fafb', icon: '⚪' }
};

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: '#ca8a04', bg: '#fefce8' },
  approved: { label: 'Approved', color: '#16a34a', bg: '#f0fdf4' },
  dismissed: { label: 'Dismissed', color: '#6b7280', bg: '#f9fafb' },
  applied: { label: 'Applied', color: '#2563eb', bg: '#eff6ff' },
  rolled_back: { label: 'Rolled Back', color: '#dc2626', bg: '#fef2f2' },
  expired: { label: 'Expired', color: '#9ca3af', bg: '#f3f4f6' }
};

const TYPE_CONFIG = {
  prompt_edit: { label: 'Prompt Edit', icon: '✏️' },
  config_change: { label: 'Config Change', icon: '⚙️' },
  knowledge_gap_flag: { label: 'Knowledge Gap', icon: '📚' },
  threshold_adjust: { label: 'Threshold Adjust', icon: '📊' }
};

const SealCandidates = () => {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState('pending');
  const [typeFilter, setTypeFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [sortBy, setSortBy] = useState('priority');

  // Detail modal
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewing, setReviewing] = useState(false);

  // Apply / Rollback
  const [applying, setApplying] = useState(false);

  // Generation
  const [generating, setGenerating] = useState(false);
  const [genResult, setGenResult] = useState(null);

  const LIMIT = 20;

  const fetchCandidates = useCallback(async (newOffset = 0) => {
    try {
      const params = { limit: LIMIT, offset: newOffset, sort: sortBy };
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.candidate_type = typeFilter;
      if (priorityFilter) params.priority = priorityFilter;

      const res = await adminApi.get('/api/seal/candidates', { params });
      if (res.data?.success) {
        const d = res.data.data;
        setCandidates(d.candidates || []);
        setTotal(d.total || 0);
        setOffset(newOffset);
        setHasMore(d.hasMore || false);
      }
    } catch (err) {
      console.error('[SEAL Candidates] Fetch error:', err);
      setError('Failed to load candidates');
    }
  }, [statusFilter, typeFilter, priorityFilter, sortBy]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await adminApi.get('/api/seal/candidates/stats');
      if (res.data?.success) {
        setStats(res.data.data);
      }
    } catch (err) {
      console.error('[SEAL Candidates] Stats error:', err);
    }
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchCandidates(0), fetchStats()]);
      setLoading(false);
    };
    loadAll();
  }, [fetchCandidates, fetchStats]);

  const handleReview = async (candidateId, action) => {
    setReviewing(true);
    try {
      const res = await adminApi.put(`/api/seal/candidates/${candidateId}/review`, {
        action,
        notes: reviewNotes || null
      });
      if (res.data?.success) {
        setSelectedCandidate(null);
        setReviewNotes('');
        await Promise.all([fetchCandidates(offset), fetchStats()]);
      }
    } catch (err) {
      console.error('[SEAL Candidates] Review error:', err);
      setError(`Failed to ${action} candidate`);
    }
    setReviewing(false);
  };

  const handleApply = async (candidateId) => {
    if (!window.confirm('Apply this candidate to the live system? A snapshot will be created for rollback.')) return;
    setApplying(true);
    try {
      const res = await adminApi.post(`/api/seal/candidates/${candidateId}/apply`);
      if (res.data?.success) {
        setSelectedCandidate(null);
        await Promise.all([fetchCandidates(offset), fetchStats()]);
      } else {
        setError(res.data?.error || 'Failed to apply candidate');
      }
    } catch (err) {
      console.error('[SEAL Candidates] Apply error:', err);
      setError(err.response?.data?.error || 'Failed to apply candidate');
    }
    setApplying(false);
  };

  const handleRollback = async (candidateId) => {
    if (!window.confirm('Roll back this candidate? The prompt will be restored to its previous state.')) return;
    setApplying(true);
    try {
      const res = await adminApi.post(`/api/seal/candidates/${candidateId}/rollback`);
      if (res.data?.success) {
        setSelectedCandidate(null);
        await Promise.all([fetchCandidates(offset), fetchStats()]);
      } else {
        setError(res.data?.error || 'Failed to rollback candidate');
      }
    } catch (err) {
      console.error('[SEAL Candidates] Rollback error:', err);
      setError(err.response?.data?.error || 'Failed to rollback candidate');
    }
    setApplying(false);
  };

  const handleGenerate = async (dryRun = false) => {
    setGenerating(true);
    setGenResult(null);
    try {
      const res = await adminApi.post('/api/seal/candidates/generate', {
        period: '7d',
        dryRun
      });
      if (res.data?.success) {
        setGenResult(res.data.data);
        if (!dryRun) {
          await Promise.all([fetchCandidates(0), fetchStats()]);
        }
      }
    } catch (err) {
      console.error('[SEAL Candidates] Generation error:', err);
      setError('Failed to run candidate generation');
    }
    setGenerating(false);
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <AdminLayout showBackButton backPath="/admin/seal/dashboard">
        <div className="seal-candidates">
          <div className="seal-loading">
            <div className="spinner"></div>
            <p>Loading SEAL Candidates...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout showBackButton backPath="/admin/seal/dashboard">
      <div className="seal-candidates">
        {/* Top Bar */}
        <div className="seal-topbar">
          <div className="seal-topbar-left">
            <h1>
              <span className="header-icon">📋</span>
              SEAL Candidates
            </h1>
            {stats && (
              <span className="seal-mode-badge">
                {stats.pending || 0} pending
              </span>
            )}
          </div>
          <div className="seal-topbar-right">
            <div className="seal-nav-actions">
              <button
                className="btn btn-secondary"
                onClick={() => handleGenerate(true)}
                disabled={generating}
              >
                {generating ? '⟳ Running...' : '🔍 Dry Run'}
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handleGenerate(false)}
                disabled={generating}
              >
                {generating ? '⟳ Generating...' : '⚡ Generate Candidates'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => navigate('/admin/seal/dashboard')}
              >
                Dashboard
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="error-banner">
            <span>⚠️ {error}</span>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {/* Generation Result */}
        {genResult && (
          <div className="seal-section gen-result-banner">
            <div className="gen-result-header">
              <h3>Generation {genResult.dryRun ? '(Dry Run)' : 'Complete'}</h3>
              <button className="btn-close" onClick={() => setGenResult(null)}>×</button>
            </div>
            <div className="gen-result-stats">
              <span className="gen-stat">
                <strong>{genResult.generated || 0}</strong> generated
              </span>
              <span className="gen-stat">
                <strong>{genResult.skipped_duplicates || 0}</strong> duplicates skipped
              </span>
              <span className="gen-stat">
                <strong>{genResult.errors?.length || 0}</strong> errors
              </span>
            </div>
            {genResult.candidates?.length > 0 && (
              <div className="gen-result-list">
                {genResult.candidates.map((c, i) => (
                  <div key={i} className="gen-result-item">
                    <span className="gen-result-type">{TYPE_CONFIG[c.type]?.icon || '📌'}</span>
                    <span className="gen-result-title">{c.title}</span>
                    <span className={`priority-badge priority-${c.priority}`}>
                      {c.priority}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {genResult.aborted && (
              <div className="gen-aborted">
                Aborted: {genResult.reason?.replace(/_/g, ' ')}
              </div>
            )}
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="seal-section">
            <h2 className="seal-section-title">Queue Overview</h2>
            <div className="seal-metrics-row">
              <div className="seal-metric-card" onClick={() => setStatusFilter('pending')}
                   style={{ cursor: 'pointer', borderColor: statusFilter === 'pending' ? '#ca8a04' : undefined }}>
                <span className="metric-value">{stats.pending || 0}</span>
                <span className="metric-label">Pending</span>
              </div>
              <div className="seal-metric-card" onClick={() => setStatusFilter('approved')}
                   style={{ cursor: 'pointer', borderColor: statusFilter === 'approved' ? '#16a34a' : undefined }}>
                <span className="metric-value">{stats.approved || 0}</span>
                <span className="metric-label">Approved</span>
              </div>
              <div className="seal-metric-card" onClick={() => setStatusFilter('dismissed')}
                   style={{ cursor: 'pointer', borderColor: statusFilter === 'dismissed' ? '#6b7280' : undefined }}>
                <span className="metric-value">{stats.dismissed || 0}</span>
                <span className="metric-label">Dismissed</span>
              </div>
              <div className="seal-metric-card" onClick={() => setStatusFilter('applied')}
                   style={{ cursor: 'pointer', borderColor: statusFilter === 'applied' ? '#2563eb' : undefined }}>
                <span className="metric-value">{stats.applied || 0}</span>
                <span className="metric-label">Applied</span>
              </div>
              <div className="seal-metric-card" onClick={() => setStatusFilter('')}
                   style={{ cursor: 'pointer', borderColor: statusFilter === '' ? '#4f46e5' : undefined }}>
                <span className="metric-value">{stats.total || 0}</span>
                <span className="metric-label">Total</span>
              </div>
            </div>

            {/* Pending breakdown */}
            {stats.pending > 0 && stats.pending_by_priority && (
              <div className="pending-breakdown">
                {Object.entries(stats.pending_by_priority).map(([p, count]) => (
                  <span key={p} className={`priority-chip priority-${p}`}>
                    {PRIORITY_CONFIG[p]?.icon} {count} {p}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="seal-section">
          <div className="seal-section-header">
            <h2 className="seal-section-title">
              Candidates ({total})
            </h2>
            <div className="seal-filters">
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="filter-select"
              >
                <option value="">All Types</option>
                <option value="prompt_edit">Prompt Edit</option>
                <option value="config_change">Config Change</option>
                <option value="knowledge_gap_flag">Knowledge Gap</option>
                <option value="threshold_adjust">Threshold Adjust</option>
              </select>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="filter-select"
              >
                <option value="">All Priorities</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="filter-select"
              >
                <option value="priority">By Priority</option>
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
              </select>
            </div>
          </div>

          {/* Candidate List */}
          {candidates.length === 0 ? (
            <div className="seal-empty">
              No candidates found{statusFilter ? ` with status "${statusFilter}"` : ''}.
              {statusFilter === 'pending' && ' Use "Generate Candidates" to analyze observation data.'}
            </div>
          ) : (
            <div className="candidate-list">
              {candidates.map(c => {
                const pConfig = PRIORITY_CONFIG[c.priority] || PRIORITY_CONFIG.medium;
                const sConfig = STATUS_CONFIG[c.status] || STATUS_CONFIG.pending;
                const tConfig = TYPE_CONFIG[c.candidate_type] || { label: c.candidate_type, icon: '📌' };

                return (
                  <div
                    key={c.candidate_id}
                    className="candidate-card"
                    onClick={() => { setSelectedCandidate(c); setReviewNotes(''); }}
                    style={{ borderLeftColor: pConfig.color }}
                  >
                    <div className="candidate-card-header">
                      <div className="candidate-card-meta">
                        <span className="candidate-type-badge">
                          {tConfig.icon} {tConfig.label}
                        </span>
                        <span className={`priority-badge priority-${c.priority}`}>
                          {pConfig.icon} {pConfig.label}
                        </span>
                        <span
                          className="status-badge"
                          style={{ color: sConfig.color, backgroundColor: sConfig.bg }}
                        >
                          {sConfig.label}
                        </span>
                      </div>
                      <span className="candidate-time">{formatTime(c.created_at)}</span>
                    </div>
                    <h3 className="candidate-title">{c.title}</h3>
                    <p className="candidate-desc">{c.description}</p>
                    <div className="candidate-card-footer">
                      <span className="candidate-process">{c.process}</span>
                      {c.vertical && <span className="candidate-vertical">{c.vertical}</span>}
                      <span className="candidate-confidence">
                        Confidence: {Math.round((c.confidence_score || 0) * 100)}%
                      </span>
                      <span className="candidate-obs">
                        {c.observation_count || 0} observations
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {total > LIMIT && (
            <div className="candidate-pagination">
              <button
                className="btn btn-secondary"
                disabled={offset === 0}
                onClick={() => fetchCandidates(Math.max(0, offset - LIMIT))}
              >
                ← Previous
              </button>
              <span className="pagination-info">
                {offset + 1}–{Math.min(offset + LIMIT, total)} of {total}
              </span>
              <button
                className="btn btn-secondary"
                disabled={!hasMore}
                onClick={() => fetchCandidates(offset + LIMIT)}
              >
                Next →
              </button>
            </div>
          )}
        </div>

        {/* Detail / Review Modal */}
        {selectedCandidate && (
          <div className="candidate-modal-overlay" onClick={() => setSelectedCandidate(null)}>
            <div className="candidate-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{selectedCandidate.title}</h2>
                <button className="btn-close" onClick={() => setSelectedCandidate(null)}>×</button>
              </div>

              <div className="modal-body">
                {/* Meta badges */}
                <div className="modal-meta">
                  <span className="candidate-type-badge">
                    {TYPE_CONFIG[selectedCandidate.candidate_type]?.icon}{' '}
                    {TYPE_CONFIG[selectedCandidate.candidate_type]?.label}
                  </span>
                  <span className={`priority-badge priority-${selectedCandidate.priority}`}>
                    {PRIORITY_CONFIG[selectedCandidate.priority]?.icon}{' '}
                    {PRIORITY_CONFIG[selectedCandidate.priority]?.label}
                  </span>
                  <span
                    className="status-badge"
                    style={{
                      color: STATUS_CONFIG[selectedCandidate.status]?.color,
                      backgroundColor: STATUS_CONFIG[selectedCandidate.status]?.bg
                    }}
                  >
                    {STATUS_CONFIG[selectedCandidate.status]?.label}
                  </span>
                  <span className="candidate-confidence">
                    Confidence: {Math.round((selectedCandidate.confidence_score || 0) * 100)}%
                  </span>
                </div>

                {/* Description */}
                <div className="modal-section">
                  <h4>Description</h4>
                  <p>{selectedCandidate.description}</p>
                </div>

                {/* Evidence */}
                {selectedCandidate.evidence && (
                  <div className="modal-section">
                    <h4>Evidence</h4>
                    <div className="evidence-grid">
                      {selectedCandidate.evidence.failure_rate != null && (
                        <div className="evidence-item">
                          <span className="evidence-label">Failure Rate</span>
                          <span className="evidence-value">
                            {Math.round(selectedCandidate.evidence.failure_rate * 100)}%
                          </span>
                        </div>
                      )}
                      {selectedCandidate.evidence.avg_confidence != null && (
                        <div className="evidence-item">
                          <span className="evidence-label">Avg Confidence</span>
                          <span className="evidence-value">
                            {Math.round(selectedCandidate.evidence.avg_confidence * 100)}%
                          </span>
                        </div>
                      )}
                      {selectedCandidate.evidence.fallback_rate != null && (
                        <div className="evidence-item">
                          <span className="evidence-label">Fallback Rate</span>
                          <span className="evidence-value">
                            {Math.round(selectedCandidate.evidence.fallback_rate * 100)}%
                          </span>
                        </div>
                      )}
                    </div>
                    {selectedCandidate.evidence.sample_queries?.length > 0 && (
                      <div className="evidence-queries">
                        <span className="evidence-label">Sample Queries:</span>
                        <ul>
                          {selectedCandidate.evidence.sample_queries.map((q, i) => (
                            <li key={i} className="text-mono">{q}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Suggested Change (prompt_edit) */}
                {selectedCandidate.suggested_change?.type && (
                  <div className="modal-section">
                    <h4>Suggested Change</h4>
                    <div className="suggested-change">
                      <div className="change-row">
                        <span className="change-label">Type:</span>
                        <span className="change-value">{selectedCandidate.suggested_change.type}</span>
                      </div>
                      {selectedCandidate.suggested_change.target_section && (
                        <div className="change-row">
                          <span className="change-label">Target Section:</span>
                          <span className="change-value">{selectedCandidate.suggested_change.target_section}</span>
                        </div>
                      )}
                      {selectedCandidate.suggested_change.suggested_text && (
                        <div className="change-row">
                          <span className="change-label">Suggested Text:</span>
                          <pre className="change-text">{selectedCandidate.suggested_change.suggested_text}</pre>
                        </div>
                      )}
                      {selectedCandidate.suggested_change.reasoning && (
                        <div className="change-row">
                          <span className="change-label">Reasoning:</span>
                          <span className="change-value">{selectedCandidate.suggested_change.reasoning}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Config Change */}
                {selectedCandidate.config_change?.reasoning && (
                  <div className="modal-section">
                    <h4>Config Change</h4>
                    <div className="suggested-change">
                      <div className="change-row">
                        <span className="change-label">Config Key:</span>
                        <span className="change-value text-mono">{selectedCandidate.config_key}</span>
                      </div>
                      <div className="change-row">
                        <span className="change-label">Current Value:</span>
                        <span className="change-value text-mono">
                          {JSON.stringify(selectedCandidate.config_change.current_value)}
                        </span>
                      </div>
                      <div className="change-row">
                        <span className="change-label">Suggested Value:</span>
                        <span className="change-value text-mono" style={{ color: '#16a34a' }}>
                          {JSON.stringify(selectedCandidate.config_change.suggested_value)}
                        </span>
                      </div>
                      <div className="change-row">
                        <span className="change-label">Reasoning:</span>
                        <span className="change-value">{selectedCandidate.config_change.reasoning}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Source Info */}
                <div className="modal-section">
                  <h4>Source</h4>
                  <div className="source-info">
                    <span>Process: <strong>{selectedCandidate.process}</strong></span>
                    {selectedCandidate.vertical && (
                      <span>Vertical: <strong>{selectedCandidate.vertical}</strong></span>
                    )}
                    <span>Observations: <strong>{selectedCandidate.observation_count || 0}</strong></span>
                    <span>Impact: <strong>{selectedCandidate.estimated_impact || 'medium'}</strong></span>
                    <span>Created: <strong>{formatTime(selectedCandidate.created_at)}</strong></span>
                    {selectedCandidate.expires_at && (
                      <span>Expires: <strong>{formatTime(selectedCandidate.expires_at)}</strong></span>
                    )}
                  </div>
                </div>

                {/* Review info if already reviewed */}
                {selectedCandidate.reviewed_by && (
                  <div className="modal-section">
                    <h4>Review</h4>
                    <div className="source-info">
                      <span>Reviewed by: <strong>{selectedCandidate.reviewed_by}</strong></span>
                      <span>Reviewed at: <strong>{formatTime(selectedCandidate.reviewed_at)}</strong></span>
                      {selectedCandidate.review_notes && (
                        <span>Notes: {selectedCandidate.review_notes}</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Review Actions (only for pending) */}
                {selectedCandidate.status === 'pending' && (
                  <div className="modal-section review-section">
                    <h4>Review This Candidate</h4>
                    <textarea
                      className="review-notes-input"
                      placeholder="Review notes (optional)..."
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      rows={3}
                    />
                    <div className="review-actions">
                      <button
                        className="btn btn-approve"
                        onClick={() => handleReview(selectedCandidate.candidate_id, 'approve')}
                        disabled={reviewing}
                      >
                        {reviewing ? '...' : '✓ Approve'}
                      </button>
                      <button
                        className="btn btn-dismiss"
                        onClick={() => handleReview(selectedCandidate.candidate_id, 'dismiss')}
                        disabled={reviewing}
                      >
                        {reviewing ? '...' : '✕ Dismiss'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Apply Action (only for approved prompt_edit candidates) */}
                {selectedCandidate.status === 'approved' && selectedCandidate.candidate_type === 'prompt_edit' && (
                  <div className="modal-section review-section">
                    <h4>Apply to Live System</h4>
                    <p className="apply-warning">
                      This will modify the live PromptTemplate. A snapshot will be saved for rollback.
                    </p>
                    <div className="review-actions">
                      <button
                        className="btn btn-apply"
                        onClick={() => handleApply(selectedCandidate.candidate_id)}
                        disabled={applying}
                      >
                        {applying ? '⟳ Applying...' : '🚀 Apply Candidate'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Rollback Action (only for applied candidates) */}
                {selectedCandidate.status === 'applied' && (
                  <div className="modal-section review-section">
                    <h4>Rollback</h4>
                    <p className="apply-warning">
                      This will restore the PromptTemplate to its state before this candidate was applied.
                    </p>
                    <div className="review-actions">
                      <button
                        className="btn btn-rollback"
                        onClick={() => handleRollback(selectedCandidate.candidate_id)}
                        disabled={applying}
                      >
                        {applying ? '⟳ Rolling back...' : '↩ Rollback'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default SealCandidates;
